// src/pages/EadTrilhaPage/EadTrilhaPage.jsx

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useTurmaService } from '../../services/turmaServices';
import { useEmpresaService } from '../../services/empresaServices';
import { useParticipanteService } from '../../services/participanteServices';

import { useTrilhaService } from '../../services/trilhaServices';
import { useModuloService } from '../../services/moduloServices';
import { useModuloSecaoService } from '../../services/moduloSecaoServices';
import { useModuloConteudoService } from '../../services/moduloConteudoServices';
import { useTesteService } from '../../services/testeServices';

// Constantes
import { URLS } from '../../constants/urls';

// Estilos
import styles from './eadtrilhapage.module.css';

// ✅ ATIVE AQUI
const DEBUG = true;

const debug = {
  log: (...args) => { if (DEBUG) console.log('[EadTrilhaPage]', ...args); },
  warn: (...args) => { if (DEBUG) console.warn('[EadTrilhaPage]', ...args); },
  error: (...args) => { if (DEBUG) console.error('[EadTrilhaPage]', ...args); },
  table: (obj) => { if (DEBUG && console.table) console.table(obj); },
  group: (...args) => { if (DEBUG && console.group) console.group('[EadTrilhaPage]', ...args); },
  groupCollapsed: (...args) => { if (DEBUG && console.groupCollapsed) console.groupCollapsed('[EadTrilhaPage]', ...args); },
  groupEnd: () => { if (DEBUG && console.groupEnd) console.groupEnd(); },
  time: (label) => { if (DEBUG && console.time) console.time(`[EadTrilhaPage] ${label}`); },
  timeEnd: (label) => { if (DEBUG && console.timeEnd) console.timeEnd(`[EadTrilhaPage] ${label}`); },
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normalizeText = (v) => (v === null || typeof v === 'undefined' ? '' : String(v));

function isAbsoluteLike(url) {
  const u = String(url || '').trim();
  return (
    /^https?:\/\//i.test(u) ||
    /^data:/i.test(u) ||
    /^blob:/i.test(u) ||
    /^\/\//.test(u)
  );
}

function joinBaseAndPath(base, path) {
  const b = String(base || '');
  const p = String(path || '');
  if (!b) return p;
  if (!p) return '';

  const b2 = b.endsWith('/') ? b.slice(0, -1) : b;
  const p2 = p.startsWith('/') ? p.slice(1) : p;
  return `${b2}/${p2}`;
}

// Regra: se vier relativo, prefixa com URLS.IMG_PATH
function toAssetUrl(maybePath) {
  const raw = String(maybePath || '').trim();
  if (!raw) return '';

  if (isAbsoluteLike(raw)) return raw;

  const base = String(URLS?.IMG_PATH || '').trim();
  if (base && raw.startsWith(base)) return raw;

  return joinBaseAndPath(base, raw);
}

function looksLikePdf(pathOrUrl) {
  const v = String(pathOrUrl || '').toLowerCase();
  return v.endsWith('.pdf') || v.includes('/reports/') || v.includes('reports/');
}

function PdfViewerModal({ open, title, url, onClose }) {
  // Fecha com ESC
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.pdfOverlay} onMouseDown={onClose}>
      <div
        className={styles.pdfModal}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.pdfHeader}>
          <div className={styles.pdfTitle}>
            {title || 'Visualizador de PDF'}
          </div>

          <div className={styles.pdfActions}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={styles.pdfActionBtn}
              title="Abrir em nova aba"
            >
              Abrir
            </a>

            <a
              href={url}
              download
              className={styles.pdfActionBtnOutline}
              title="Baixar PDF"
            >
              Baixar
            </a>

            <button
              type="button"
              className={styles.pdfCloseBtn}
              onClick={onClose}
              title="Fechar (ESC)"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.pdfBody}>
          <iframe
            title={title || 'pdf'}
            src={url}
            className={styles.pdfFrame}
          />
        </div>
      </div>
    </div>
  );
}

function EadTrilhaPage() {
  const { idTurma } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { logout, usuario } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();

  const { obterTurmaPorId, associarParticipanteATurma } = useTurmaService();
  const { obterEmpresaPorId } = useEmpresaService();
  const { listarParticipantesPorTurma } = useParticipanteService();

  const { obterTrilhaPorId } = useTrilhaService();
  const { listarModulosPorTrilha } = useModuloService();
  const { listarSecoesPorModulo } = useModuloSecaoService();
  const { listarConteudosPorSecao } = useModuloConteudoService();
  const { obterTestePorId } = useTesteService();

  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);

  const [trilha, setTrilha] = useState(null);
  const [modulos, setModulos] = useState([]);

  const [selectedModuloId, setSelectedModuloId] = useState(null);

  // Cache de detalhes por módulo (evita refetch ao voltar/abrir de novo)
  // ✅ agora também guarda testeEstrutura (questions etc)
  const [detalhesCache, setDetalhesCache] = useState({});

  // PDF viewer
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: '',
    title: ''
  });

  const openPdf = (url, title) => {
    if (!url) return;
    debug.log('openPdf ->', { url, title });
    setPdfViewer({ open: true, url, title: title || 'PDF' });
  };

  const closePdf = () => setPdfViewer({ open: false, url: '', title: '' });

  // 🔎 contador local de loader (ajuda a detectar “vazamento”)
  const loaderCountRef = useRef(0);
  const showL = (msg) => {
    loaderCountRef.current += 1;
    debug.log(`showLoader(${loaderCountRef.current}) ->`, msg);
    showLoader(msg);
  };
  const hideL = () => {
    loaderCountRef.current = Math.max(0, loaderCountRef.current - 1);
    debug.log(`hideLoader(${loaderCountRef.current})`);
    hideLoader();
  };

  const selectedModulo = useMemo(() => {
    if (!selectedModuloId) return null;
    return modulos.find((m) => String(m.id) === String(selectedModuloId)) || null;
  }, [selectedModuloId, modulos]);

  const detalhesSelecionado = useMemo(() => {
    if (!selectedModuloId) return null;
    return detalhesCache[selectedModuloId] || null;
  }, [selectedModuloId, detalhesCache]);

  // ===== Helpers =====
  const sortByOrdem = (a, b) => {
    const oa = Number(a?.ordem ?? 0);
    const ob = Number(b?.ordem ?? 0);
    if (oa !== ob) return oa - ob;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  };

  // ✅ (NOVO) módulo fechado -> desabilitar card
  const isModuloFechado = (m) => Boolean(m?.fechado);

  const getModuloDataAberturaLabel = (m) => {
    const d = m?.dataAbertura ?? m?.data_abertura ?? '';
    return d ? String(d) : '';
  };

  // ✅ Redireciona para login mantendo “from”
  const redirectToLogin = (reason = '') => {
    debug.groupCollapsed('redirectToLogin');
    debug.log('reason:', reason);
    debug.log('idTurma:', idTurma);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    debug.groupEnd();

    navigate(
      `/participante/login?turma=${encodeURIComponent(idTurma || '')}&dest=ead`,
      { replace: true, state: { from: location } }
    );
  };

  // YouTube: converte qualquer URL (watch/youtu.be/shorts/embed) para embed
  const toYouTubeEmbedUrl = (inputUrl) => {
    if (!inputUrl) return null;

    try {
      const u = new URL(inputUrl);

      if (u.hostname.includes('youtu.be')) {
        const videoId = u.pathname.replace('/', '').trim();
        if (!videoId) return null;
        const list = u.searchParams.get('list');
        return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
      }

      if (u.hostname.includes('youtube.com')) {
        if (u.pathname === '/watch') {
          const videoId = u.searchParams.get('v');
          if (!videoId) return null;
          const list = u.searchParams.get('list');
          return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
        }

        if (u.pathname.startsWith('/shorts/')) {
          const videoId = u.pathname.split('/shorts/')[1]?.split('/')[0]?.trim();
          if (!videoId) return null;
          return `https://www.youtube.com/embed/${videoId}`;
        }

        if (u.pathname.startsWith('/embed/')) return inputUrl;
      }

      return null;
    } catch (e) {
      debug.warn('toYouTubeEmbedUrl: URL inválida:', inputUrl, e);
      return null;
    }
  };

  // ====== (NOVO) LÓGICA DE TESTE EXTERNO DENTRO DO MÓDULO ======
  const EMBARALHAR_OPCOES = false;

  const [respostasTeste, setRespostasTeste] = useState({});
  const [paginaAtualTeste, setPaginaAtualTeste] = useState(0);
  const [embaralhamentoAtualTeste, setEmbaralhamentoAtualTeste] = useState({});
  const [dragStateTeste, setDragStateTeste] = useState({ fromIdx: null, overIdx: null });

  const testeEstrutura = detalhesSelecionado?.testeEstrutura || null;
  const totalQuestoesTeste = testeEstrutura?.questions?.length || 0;

  const perguntaAtualTeste = testeEstrutura?.questions?.[paginaAtualTeste];
  const respostaAtualTeste = respostasTeste[paginaAtualTeste];

  const handleChangeTeste = (index, value) => {
    setRespostasTeste(prev => ({ ...prev, [index]: value }));
  };

  const movePriorityItemTeste = (fromIdx, toIdx) => {
    setRespostasTeste(prev => {
      const atualPergunta = testeEstrutura?.questions?.[paginaAtualTeste];
      if (atualPergunta?.type !== 'priority') return prev;

      const currentOrder = Array.isArray(prev[paginaAtualTeste])
        ? prev[paginaAtualTeste]
        : Object.keys((atualPergunta.options) || {});

      if (toIdx < 0 || toIdx >= currentOrder.length) return prev;
      const nova = [...currentOrder];
      const [m] = nova.splice(fromIdx, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualTeste]: nova };
    });
  };

  const handleClickUpTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemTeste(idx, idx - 1);
  };

  const handleClickDownTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemTeste(idx, idx + 1);
  };

  const isPerguntaRespondida = (perguntaObj, respostaVal) => {
    if (!perguntaObj) return false;
    const tipo = perguntaObj.type;

    if (tipo === 'closed') return (respostaVal ?? '').toString().trim() !== '';
    if (tipo === 'priority') return Array.isArray(respostaVal) && respostaVal.length > 0;
    if (tipo === 'scale') return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
    if (tipo === 'open') {
      if (perguntaObj.hasDate) {
        if (!respostaVal || typeof respostaVal !== 'object') return false;
        const text = (respostaVal.text ?? '').toString().trim();
        const date = (respostaVal.date ?? '').toString().trim();
        return text !== '' && date !== '';
      }
      return (respostaVal ?? '').toString().trim() !== '';
    }
    return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
  };

  const perguntaRespondidaTeste = isPerguntaRespondida(perguntaAtualTeste, respostaAtualTeste);

  const priorityOrderTeste = (perguntaAtualTeste?.type === 'priority')
    ? (Array.isArray(respostasTeste[paginaAtualTeste])
      ? respostasTeste[paginaAtualTeste]
      : Object.keys(perguntaAtualTeste?.options || {}))
    : [];

  // Reset do estado do teste quando troca de módulo
  useEffect(() => {
    setRespostasTeste({});
    setPaginaAtualTeste(0);
    setEmbaralhamentoAtualTeste({});
    setDragStateTeste({ fromIdx: null, overIdx: null });
  }, [selectedModuloId]);

  // Embaralhamento + valores iniciais (igual ao ResponderTestePage)
  useEffect(() => {
    const p = testeEstrutura?.questions?.[paginaAtualTeste];
    if (!p) {
      setEmbaralhamentoAtualTeste({});
      return;
    }

    if (p.type === 'closed') {
      const opcoesOriginais = Object.entries(p.options || {});
      const textos = opcoesOriginais.map(([, texto]) => texto);
      const textosProcessados = EMBARALHAR_OPCOES ? [...textos].sort(() => Math.random() - 0.5) : textos;

      const novaOrdem = {};
      Object.keys(p.options || {}).forEach((letra, index) => {
        novaOrdem[letra] = textosProcessados[index];
      });
      setEmbaralhamentoAtualTeste(novaOrdem);

      setRespostasTeste(prev => (prev[paginaAtualTeste] !== undefined ? prev : { ...prev, [paginaAtualTeste]: '' }));
    } else if (p.type === 'priority') {
      setRespostasTeste(prev => {
        if (Array.isArray(prev[paginaAtualTeste])) return prev;
        const initialOrder = Object.keys(p.options || {});
        return { ...prev, [paginaAtualTeste]: initialOrder };
      });
      setEmbaralhamentoAtualTeste({});
    } else if (p.type === 'scale') {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => {
        if (prev[paginaAtualTeste] !== undefined) return prev;
        const min = (p.scale && typeof p.scale.min !== 'undefined')
          ? Number(p.scale.min)
          : 0;
        return { ...prev, [paginaAtualTeste]: min };
      });
    } else if (p.type === 'open') {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => {
        if (prev[paginaAtualTeste] !== undefined) return prev;
        if (p.hasDate) return { ...prev, [paginaAtualTeste]: { text: '', date: '' } };
        return { ...prev, [paginaAtualTeste]: '' };
      });
    } else {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => (prev[paginaAtualTeste] !== undefined ? prev : { ...prev, [paginaAtualTeste]: '' }));
    }
  }, [paginaAtualTeste, testeEstrutura]);

  const onDragStartPriorityTeste = (fromIdx) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(fromIdx));
    setDragStateTeste({ fromIdx, overIdx: null });
  };

  const onDragOverPriorityTeste = (overIdx) => (e) => {
    e.preventDefault();
    setDragStateTeste(cur => ({ ...cur, overIdx }));
  };

  const onDropPriorityTeste = (toIdx) => (e) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    setRespostasTeste(prev => {
      const atual = Array.isArray(prev[paginaAtualTeste])
        ? prev[paginaAtualTeste]
        : Object.keys((testeEstrutura?.questions?.[paginaAtualTeste]?.options) || {});
      if (from === toIdx || from < 0 || toIdx < 0 || from >= atual.length || toIdx >= atual.length) return prev;
      const nova = [...atual];
      const [m] = nova.splice(from, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualTeste]: nova };
    });
    setDragStateTeste({ fromIdx: null, overIdx: null });
  };

  const onDragEndPriorityTeste = () => setDragStateTeste({ fromIdx: null, overIdx: null });

  const handleAnteriorTeste = () => {
    if (paginaAtualTeste > 0) setPaginaAtualTeste(paginaAtualTeste - 1);
  };

  const handleProximoTeste = () => {
    if (!testeEstrutura?.questions?.length) return;
    if (paginaAtualTeste >= totalQuestoesTeste - 1) return;

    const p = testeEstrutura.questions[paginaAtualTeste];
    const r = respostasTeste[paginaAtualTeste];
    if (isPerguntaRespondida(p, r)) setPaginaAtualTeste(paginaAtualTeste + 1);
  };

  const handleSubmitTeste = (e) => {
    e?.preventDefault?.();

    if (!usuario?.id) {
      showToast('Faça login novamente para enviar suas respostas.', 3500, 'error');
      return redirectToLogin('handleSubmitTeste: usuario.id ausente');
    }

    if (!testeEstrutura?.questions || !selectedModulo?.idTeste) {
      showToast('Teste inválido. Recarregue a página.', 3500, 'error');
      return;
    }

    // ✅ mesmo formato do ResponderTestePage
    const respostasFormatadas = Object.entries(respostasTeste).map(([indiceStr, valor]) => {
      const indice = Number(indiceStr);
      const perguntaObj = testeEstrutura.questions[indice];

      if (!perguntaObj) {
        return { pergunta: '(desconhecida)', resposta: valor, textoResposta: valor };
      }

      const perguntaId = perguntaObj.id ?? perguntaObj.questionId ?? perguntaObj.qid ?? null;

      if (perguntaObj.type === 'priority') {
        const orderLetters = Array.isArray(valor) ? valor : Object.keys(perguntaObj.options || {});
        const orderTexts = orderLetters.map((L) => (perguntaObj.options || {})[L]);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'priority',
          resposta: orderLetters,
          textoResposta: orderTexts
        };
      }

      if (perguntaObj.type === 'closed') {
        const letra = valor;
        const textoResposta = (perguntaObj.options || {})[letra];
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'closed',
          resposta: letra,
          textoResposta
        };
      }

      if (perguntaObj.type === 'scale') {
        const numVal = Number(valor);
        const labels = (perguntaObj.scale && perguntaObj.scale.labels) || {};
        const textoResposta = labels[numVal] ?? String(numVal);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'scale',
          resposta: numVal,
          textoResposta
        };
      }

      if (perguntaObj.type === 'open') {
        let texto = '';
        let dataResposta = undefined;

        if (valor && typeof valor === 'object') {
          texto = (valor.text ?? '').toString();
          dataResposta = (valor.date ?? '') || undefined;
        } else {
          texto = (valor ?? '').toString();
        }

        const base = {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'open',
          resposta: texto,
          textoResposta: texto
        };

        if (perguntaObj.hasDate && dataResposta) {
          return { ...base, dataResposta };
        }

        return base;
      }

      return {
        id: perguntaId || undefined,
        pergunta: perguntaObj.question,
        tipo: perguntaObj.type || 'desconhecido',
        resposta: valor,
        textoResposta: valor
      };
    });

    // ✅ por enquanto: só loga
    console.log('[EadTrilhaPage] SUBMIT TESTE EXTERNO -> respostasFormatadas:', respostasFormatadas);

    showToast('Respostas montadas! Veja o console.log.', 3500, 'success');
  };

  // ===== LOGS DE MOUNT / PARAMS =====
  useEffect(() => {
    debug.groupCollapsed('mount');
    debug.log('params.idTurma:', idTurma);
    debug.log('usuario:', usuario);
    debug.log('URLS.IMG_PATH:', URLS?.IMG_PATH);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    try {
      debug.log('idTurma decodeURIComponent:', decodeURIComponent(idTurma || ''));
    } catch (e) {
      debug.warn('decodeURIComponent falhou:', e);
    }
    debug.groupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== 1) Fluxo principal =====
  useEffect(() => {
    let cancelled = false;

    const carregar = async () => {
      debug.groupCollapsed('carregar (fluxo principal)');
      debug.log('start -> idTurma:', idTurma, 'usuario.id:', usuario?.id);

      if (!idTurma) {
        debug.warn('early return: idTurma vazio');
        debug.groupEnd();
        return;
      }

      if (!usuario?.id) {
        debug.warn('early return: usuário não logado');
        showToast('Faça login para acessar a trilha.', 3500, 'error');
        redirectToLogin('fluxo principal: usuario.id ausente');
        debug.groupEnd();
        return;
      }

      showL('Buscando dados da turma...');
      debug.time('fluxo principal total');

      try {
        // 1) Turma
        debug.time('obterTurmaPorId');
        debug.log('REQ obterTurmaPorId ->', { idTurma });
        const turmaRes = await obterTurmaPorId(idTurma);
        debug.timeEnd('obterTurmaPorId');
        debug.log('RES obterTurmaPorId ->', turmaRes);

        if (!turmaRes?.success) {
          showToast(turmaRes?.message || 'Erro ao carregar dados da turma.', 3000, 'error');

          if (turmaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token inválido/expirado -> logout + redirect');
            logout();
            redirectToLogin('obterTurmaPorId: token');
            return;
          }

          debug.warn('falha obterTurmaPorId -> navigate /participante/page');
          navigate('/participante/page');
          return;
        }

        const turmaData = turmaRes.data;
        debug.table({
          turma_id: turmaData?.id,
          turma_nome: turmaData?.nome,
          status: turmaData?.status,
          data_limite: turmaData?.data_limite ?? turmaData?.dataLimite,
          idEmpresa: turmaData?.idEmpresa,
          idTrilha: turmaData?.idTrilha ?? turmaData?.id_trilha,
        });

        if (cancelled) return;

        setTurma(turmaData);

        // 2) Empresa (opcional)
        try {
          debug.time('obterEmpresaPorId');
          debug.log('REQ obterEmpresaPorId ->', { idEmpresa: turmaData?.idEmpresa });
          const empRes = await obterEmpresaPorId(turmaData.idEmpresa);
          debug.timeEnd('obterEmpresaPorId');
          debug.log('RES obterEmpresaPorId ->', empRes);

          if (empRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na empresa -> logout + redirect');
            logout();
            redirectToLogin('obterEmpresaPorId: token');
            return;
          }

          if (empRes?.success && !cancelled) setEmpresa(empRes.data);
        } catch (e) {
          debug.warn('Erro ao buscar empresa (não bloqueia):', e);
        }

        // 3) Regras turma
        if (Number(turmaData?.status ?? 0) === 0) {
          debug.warn('turma inativa -> /participante/page');
          showToast('Essa turma ainda está inativa!', 4500, 'error');
          navigate('/participante/page');
          return;
        }

        const dataLimiteStr = turmaData?.data_limite || turmaData?.dataLimite;
        if (dataLimiteStr) {
          const hoje = new Date();
          const dataLimite = new Date(`${dataLimiteStr}T23:59:59`);
          debug.log('dataLimite check:', { hoje: hoje.toISOString(), dataLimite: dataLimite.toISOString() });

          if (hoje > dataLimite) {
            debug.warn('turma expirada -> /participante/page');
            showToast('O prazo para acessar essa turma expirou.', 4500, 'error');
            navigate('/participante/page');
            return;
          }
        }

        // 4) Vínculo participante x turma
        debug.time('listarParticipantesPorTurma');
        debug.log('REQ listarParticipantesPorTurma ->', { idTurma });
        const participantesRes = await listarParticipantesPorTurma(idTurma);
        debug.timeEnd('listarParticipantesPorTurma');
        debug.log('RES listarParticipantesPorTurma ->', participantesRes);

        if (!participantesRes?.success) {
          showToast(participantesRes?.message || 'Erro ao verificar vínculo do participante.', 3000, 'error');

          if (participantesRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nos participantes -> logout + redirect');
            logout();
            redirectToLogin('listarParticipantesPorTurma: token');
            return;
          }

          debug.warn('falha listarParticipantesPorTurma -> /participante/page');
          navigate('/participante/page');
          return;
        }

        const vinculado = safeArray(participantesRes.data).some((p) => p?.id === usuario.id);
        debug.log('vinculado?', vinculado);

        if (!vinculado) {
          debug.time('associarParticipanteATurma');
          debug.log('REQ associarParticipanteATurma ->', { idParticipante: usuario.id, idTurma });
          const assocRes = await associarParticipanteATurma(usuario.id, idTurma);
          debug.timeEnd('associarParticipanteATurma');
          debug.log('RES associarParticipanteATurma ->', assocRes);

          if (!assocRes?.success) {
            showToast(assocRes?.message || 'Erro ao associar você à turma.', 3000, 'error');

            if (assocRes?.message?.toLowerCase?.().includes('token')) {
              debug.warn('token no associar -> logout + redirect');
              logout();
              redirectToLogin('associarParticipanteATurma: token');
              return;
            }

            debug.warn('falha associar -> /participante/page');
            navigate('/participante/page');
            return;
          }

          showToast('Vínculo com a turma realizado com sucesso!', 2500, 'success');
        }

        // 5) Trilha
        const idTrilha = turmaData?.idTrilha ?? turmaData?.id_trilha;
        if (!idTrilha) {
          debug.warn('turma sem idTrilha -> /participante/page');
          showToast('Esta turma não possui uma trilha vinculada.', 3500, 'warning');
          navigate('/participante/page');
          return;
        }

        debug.time('obterTrilhaPorId');
        debug.log('REQ obterTrilhaPorId ->', { idTrilha });
        const trilhaRes = await obterTrilhaPorId(idTrilha);
        debug.timeEnd('obterTrilhaPorId');
        debug.log('RES obterTrilhaPorId ->', trilhaRes);

        if (!trilhaRes?.success) {
          showToast(trilhaRes?.message || 'Erro ao carregar trilha.', 3000, 'error');

          if (trilhaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na trilha -> logout + redirect');
            logout();
            redirectToLogin('obterTrilhaPorId: token');
            return;
          }

          debug.warn('falha obterTrilhaPorId -> /participante/page');
          navigate('/participante/page');
          return;
        }

        if (cancelled) return;
        setTrilha(trilhaRes.data);

        // 6) Módulos
        debug.time('listarModulosPorTrilha');
        debug.log('REQ listarModulosPorTrilha ->', { idTrilha });
        const modRes = await listarModulosPorTrilha(idTrilha);
        debug.timeEnd('listarModulosPorTrilha');
        debug.log('RES listarModulosPorTrilha ->', modRes);

        if (!modRes?.success) {
          showToast(modRes?.message || 'Erro ao carregar módulos da trilha.', 3000, 'error');

          if (modRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nos módulos -> logout + redirect');
            logout();
            redirectToLogin('listarModulosPorTrilha: token');
            return;
          }

          debug.warn('falha listarModulosPorTrilha -> /participante/page');
          navigate('/participante/page');
          return;
        }

        const lista = safeArray(modRes.data).slice().sort(sortByOrdem);
        debug.log('modulos (sorted) length:', lista.length);
        debug.table(lista.map(m => ({
          id: m?.id,
          titulo: m?.titulo,
          ordem: m?.ordem,
          idTeste: m?.idTeste,
          fechado: m?.fechado,
          data_abertura: m?.data_abertura
        })));

        if (cancelled) return;

        setModulos(lista);

        // Reseta seleção/cache ao entrar na trilha
        setSelectedModuloId(null);
        setDetalhesCache({});
      } catch (e) {
        debug.error('catch fluxo principal:', e);
        showToast('Erro inesperado ao carregar a trilha.', 3000, 'error');
        navigate('/participante/page');
      } finally {
        debug.timeEnd('fluxo principal total');
        hideL();
        debug.groupEnd();
      }
    };

    carregar();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito principal (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTurma, usuario?.id]);

  // ===== 2) Carrega detalhes do módulo selecionado =====
  useEffect(() => {
    let cancelled = false;

    const carregarDetalhesModulo = async () => {
      debug.groupCollapsed('carregarDetalhesModulo');
      debug.log('selectedModuloId:', selectedModuloId);

      if (!selectedModuloId) {
        debug.log('early return: selectedModuloId vazio');
        debug.groupEnd();
        return;
      }

      if (detalhesCache[selectedModuloId]) {
        debug.log('cache hit -> detalhes já carregados');
        debug.groupEnd();
        return;
      }

      const modulo = modulos.find((m) => String(m.id) === String(selectedModuloId));
      if (!modulo) {
        debug.warn('modulo não encontrado no state modulos');
        debug.groupEnd();
        return;
      }

      showL('Carregando módulo...');
      debug.time(`detalhes modulo ${selectedModuloId}`);

      try {
        // seções
        debug.time('listarSecoesPorModulo');
        debug.log('REQ listarSecoesPorModulo ->', { idModulo: modulo.id });
        const secoesRes = await listarSecoesPorModulo(modulo.id);
        debug.timeEnd('listarSecoesPorModulo');
        debug.log('RES listarSecoesPorModulo ->', secoesRes);

        if (!secoesRes?.success) {
          showToast(secoesRes?.message || 'Erro ao carregar seções do módulo.', 3000, 'error');

          if (secoesRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nas seções -> logout + redirect');
            logout();
            redirectToLogin('listarSecoesPorModulo: token');
            return;
          }

          return;
        }

        const secoes = safeArray(secoesRes.data).slice().sort(sortByOrdem);
        debug.log('secoes length:', secoes.length);
        debug.table(secoes.map(s => ({ id: s?.id, titulo: s?.titulo, ordem: s?.ordem })));

        // conteúdos (paralelo)
        debug.time('listarConteudosPorSecao (Promise.all)');
        const conteudosEntries = await Promise.all(
          secoes.map(async (secao) => {
            try {
              debug.log('REQ listarConteudosPorSecao ->', { idSecao: secao.id });
              const contRes = await listarConteudosPorSecao(secao.id);
              debug.log('RES listarConteudosPorSecao ->', { idSecao: secao.id, success: contRes?.success, message: contRes?.message });

              if (!contRes?.success) return [secao.id, []];

              const conteudos = safeArray(contRes.data).slice().sort(sortByOrdem);
              return [secao.id, conteudos];
            } catch (e) {
              debug.warn('Erro listarConteudosPorSecao:', { secaoId: secao?.id, e });
              return [secao.id, []];
            }
          })
        );
        debug.timeEnd('listarConteudosPorSecao (Promise.all)');

        const conteudosBySecao = Object.fromEntries(conteudosEntries);
        debug.log('conteudosBySecao keys:', Object.keys(conteudosBySecao));

        // ✅ teste externo (opcional) -> agora carrega título E estrutura
        let testeTitulo = '';
        let testeEstrutura = null;

        if (modulo?.idTeste) {
          try {
            debug.time('obterTestePorId (modulo.idTeste)');
            debug.log('REQ obterTestePorId ->', { idTeste: modulo.idTeste });
            const testeRes = await obterTestePorId(modulo.idTeste);
            debug.timeEnd('obterTestePorId (modulo.idTeste)');
            debug.log('RES obterTestePorId ->', testeRes);

            if (testeRes?.success) {
              testeTitulo = testeRes?.data?.nome || '';

              const raw = testeRes?.data?.estrutura;
              let estruturaParse = null;

              if (raw && typeof raw === 'string') {
                try { estruturaParse = JSON.parse(raw); } catch { estruturaParse = null; }
              } else if (raw && typeof raw === 'object') {
                estruturaParse = raw;
              }

              if (estruturaParse && Array.isArray(estruturaParse.questions)) {
                testeEstrutura = estruturaParse;
              } else {
                debug.warn('Estrutura do teste externo inválida/ausente (não bloqueia):', {
                  idTeste: modulo.idTeste,
                  hasEstrutura: Boolean(raw),
                });
              }
            }
          } catch (e) {
            debug.warn('Erro ao obterTestePorId (não bloqueia):', e);
          }
        }

        if (cancelled) return;

        setDetalhesCache((prev) => ({
          ...prev,
          [selectedModuloId]: { secoes, conteudosBySecao, testeTitulo, testeEstrutura },
        }));
      } catch (e) {
        debug.error('catch detalhes modulo:', e);
        showToast('Erro inesperado ao carregar módulo.', 3000, 'error');
      } finally {
        debug.timeEnd(`detalhes modulo ${selectedModuloId}`);
        hideL();
        debug.groupEnd();
      }
    };

    carregarDetalhesModulo();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito detalhes (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuloId, modulos, detalhesCache]);

  // ===== Render do conteúdo (com prefixo + PDF viewer) =====
  const renderConteudo = (c) => {
    const tipo = normalizeText(c?.tipo).toLowerCase();
    const titulo = normalizeText(c?.titulo);
    const texto = normalizeText(c?.texto);

    const urlImagemRaw = normalizeText(c?.url_imagem);
    const urlLinkRaw = normalizeText(c?.url_link);
    const urlVideo = normalizeText(c?.url_video);

    // Imagem / PDF (prefixo)
    const urlImagem = urlImagemRaw ? toAssetUrl(urlImagemRaw) : '';
    const urlLink = urlLinkRaw ? (isAbsoluteLike(urlLinkRaw) ? urlLinkRaw : toAssetUrl(urlLinkRaw)) : '';

    // Detecta PDF tanto no tipo quanto nas URLs
    const isPdf =
      tipo === 'pdf' ||
      looksLikePdf(urlLinkRaw) ||
      looksLikePdf(urlImagemRaw);

    // Lista
    let listaItens = c?.lista_itens;
    if (typeof listaItens === 'string') {
      try {
        const parsed = JSON.parse(listaItens);
        if (Array.isArray(parsed)) listaItens = parsed;
      } catch {
        // ignore
      }
    }
    listaItens = safeArray(listaItens);

    const embedUrl = urlVideo ? toYouTubeEmbedUrl(urlVideo) : null;

    // Se o PDF veio em url_imagem, prioriza isso
    const pdfUrlFinal = (() => {
      const a = urlLinkRaw || '';
      const b = urlImagemRaw || '';
      if (looksLikePdf(a)) return toAssetUrl(a);
      if (looksLikePdf(b)) return toAssetUrl(b);
      return urlLink || urlImagem || '';
    })();

    if (DEBUG) {
      debug.log('renderConteudo', {
        id: c?.id,
        tipo,
        titulo,
        urlImagemRaw,
        urlImagem,
        urlLinkRaw,
        urlLink,
        isPdf,
        pdfUrlFinal,
      });
    }

    return (
      <div key={c.id} className={styles.conteudoItem}>
        {titulo ? <div className={styles.conteudoTitulo}>{titulo}</div> : null}
        {texto ? <div className={styles.conteudoTexto}>{texto}</div> : null}

        {listaItens.length > 0 ? (
          <ul className={styles.conteudoLista}>
            {listaItens.map((it, idx) => (
              <li key={`${c.id}-li-${idx}`}>{String(it)}</li>
            ))}
          </ul>
        ) : null}

        {/* ✅ PDF: botão para abrir no modal */}
        {isPdf && pdfUrlFinal ? (
          <div className={styles.pdfBox}>
            <button
              type="button"
              className={styles.pdfOpenBtn}
              onClick={() => openPdf(pdfUrlFinal, titulo || 'Documento PDF')}
            >
              Visualizar PDF
            </button>
          </div>
        ) : null}

        {/* ✅ Imagem (prefixada) */}
        {!isPdf && urlImagem ? (
          <img
            src={urlImagem}
            alt={titulo || 'Imagem do conteúdo'}
            className={styles.conteudoImagem}
            onError={() => debug.warn('Falha ao carregar imagem:', urlImagem)}
          />
        ) : null}

        {/* ✅ Link (prefixado quando relativo) */}
        {!isPdf && urlLink ? (
          <a
            href={urlLink}
            target="_blank"
            rel="noreferrer"
            className={styles.conteudoLink}
          >
            Acessar link
          </a>
        ) : null}

        {/* ✅ Vídeo */}
        {urlVideo ? (
          embedUrl ? (
            <div className={styles.videoWrapper}>
              <iframe
                title={titulo || `video-${c.id}`}
                src={embedUrl}
                className={styles.videoFrame}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className={styles.mutedNote}>
              Link do vídeo inválido. Verifique a URL do YouTube.
            </div>
          )
        ) : null}

        {tipo && !texto && !listaItens.length && !urlImagemRaw && !urlLinkRaw && !urlVideo ? (
          <div className={styles.mutedNote}>Conteúdo do tipo “{tipo}”</div>
        ) : null}
      </div>
    );
  };

  // ✅ Loading fallback simples
  if (!turma || !trilha) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.containerTop}>
          <h1 className={styles.title}>Carregando trilha...</h1>
          {DEBUG && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
              <div><strong>idTurma:</strong> {String(idTurma || '(vazio)')}</div>
              <div><strong>usuario.id:</strong> {String(usuario?.id || '(vazio)')}</div>
              <div><strong>loaderCount:</strong> {String(loaderCountRef.current)}</div>
              <div><strong>IMG_PATH:</strong> {String(URLS?.IMG_PATH || '(vazio)')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // (teste externo) scale config
  let scaleConfigTeste = null;
  if (perguntaAtualTeste?.type === 'scale') {
    const min = Number(perguntaAtualTeste?.scale?.min ?? 0);
    const max = Number(perguntaAtualTeste?.scale?.max ?? 10);
    const labels = (perguntaAtualTeste?.scale?.labels) || {};
    const rawValue = respostasTeste[paginaAtualTeste];
    const valueNum = Number(
      rawValue !== undefined && rawValue !== null && rawValue !== ''
        ? rawValue
        : min
    );

    const ticks = [];
    for (let v = min; v <= max; v++) {
      const lbl = labels[v] ?? String(v);
      ticks.push({ value: v, label: lbl });
    }

    const minLabel = labels[min] ?? String(min);
    const maxLabel = labels[max] ?? String(max);
    const currentLabel = labels[valueNum] ?? String(valueNum);

    scaleConfigTeste = { min, max, ticks, minLabel, maxLabel, valueNum, currentLabel };
  }

  return (
    <div className={styles.pageWrapper}>
      {/* ✅ Modal PDF */}
      <PdfViewerModal
        open={pdfViewer.open}
        title={pdfViewer.title}
        url={pdfViewer.url}
        onClose={closePdf}
      />

      <div className={styles.containerTop}>
        <h1 className={styles.title}>{trilha?.titulo ? trilha.titulo : 'Trilha'}</h1>

        <div className={styles.mutedNote}>
          {empresa?.nome ? `${empresa.nome} • ` : ''}
          {turma?.nome ? turma.nome : ''}
        </div>

        {trilha?.descricao ? <div className={styles.subTitle}>{trilha.descricao}</div> : null}

        {selectedModuloId ? (
          <div className={styles.navigationArea}>
            <button
              type="button"
              className={styles.navBackButton}
              onClick={() => setSelectedModuloId(null)}
            >
              Voltar para módulos
            </button>
          </div>
        ) : null}
      </div>

      {!selectedModuloId && (
        <div className={styles.formulario}>
          {modulos.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Nenhum módulo disponível</div>
              <div className={styles.mutedNote}>
                Esta trilha ainda não possui módulos cadastrados.
              </div>
            </div>
          ) : (
            <div className={styles.modulosGrid}>
              {modulos.map((m) => {
                const locked = isModuloFechado(m);
                const dataAbertura = getModuloDataAberturaLabel(m);

                const title = locked
                  ? `Módulo ainda fechado${dataAbertura ? ` (abre em ${dataAbertura})` : ''}`
                  : (m?.descricao || '');

                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`${styles.moduloCard} ${locked ? (styles.moduloCardDisabled || '') : ''}`}
                    onClick={() => {
                      if (locked) return; // redundância, pois disabled já bloqueia
                      setSelectedModuloId(m.id);
                    }}
                    title={title}
                    disabled={locked}
                    aria-disabled={locked}
                    style={locked ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  >
                    <div className={styles.moduloCardTitle}>{m.titulo}</div>

                    {m?.descricao ? <div className={styles.moduloCardDesc}>{m.descricao}</div> : null}

                    {locked ? (
                      <div className={styles.mutedNote} style={{ marginTop: 8 }}>
                        🔒 Módulo fechado{dataAbertura ? ` • Abre em ${dataAbertura}` : ''}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedModuloId && selectedModulo && (
        <div className={styles.formulario}>
          <div className={styles.perguntaWrapper}>
            <div className={styles.pergunta}>{selectedModulo.titulo}</div>

            {selectedModulo?.descricao ? (
              <div className={styles.mutedNote}>{selectedModulo.descricao}</div>
            ) : null}

            {/* ✅ Se tem teste externo, agora monta e renderiza aqui */}
            {selectedModulo?.idTeste ? (
              <div className={styles.testeBox}>
                <div className={styles.testeTitle}>
                  {detalhesSelecionado?.testeTitulo ? detalhesSelecionado.testeTitulo : 'Teste externo'}
                </div>

                {testeEstrutura?.questions?.length ? (
                  <div className={styles.mutedNote} style={{ marginBottom: 10 }}>
                    Responda o teste abaixo e clique em <strong>Enviar</strong> (por enquanto ele só mostra no console).
                  </div>
                ) : (
                  <div className={styles.mutedNote} style={{ marginBottom: 10 }}>
                    Carregando estrutura do teste...
                  </div>
                )}

                {/* UI do teste externo */}
                {testeEstrutura?.questions?.length ? (
                  <div style={{ marginTop: 16 }}>
                    <div className={styles.perguntaWrapper}>
                      <p className={styles.pergunta}>{perguntaAtualTeste?.question}</p>

                      {perguntaAtualTeste?.type === 'closed' && (
                        <div className={styles.opcoesWrapper}>
                          {Object.keys(perguntaAtualTeste.options || {}).map((letra) => (
                            <div key={letra} className={styles.opcaoWrapper}>
                              <input
                                type="radio"
                                id={`ext_q${paginaAtualTeste}_${letra}`}
                                name={`ext_resposta-${paginaAtualTeste}`}
                                value={letra}
                                checked={respostasTeste[paginaAtualTeste] === letra}
                                onChange={() => handleChangeTeste(paginaAtualTeste, letra)}
                                className={styles.radioInput}
                                required
                              />
                              <label htmlFor={`ext_q${paginaAtualTeste}_${letra}`} className={styles.opcaoLabel}>
                                {letra}) {embaralhamentoAtualTeste[letra]}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}

                      {perguntaAtualTeste?.type === 'priority' && (
                        <div className={styles.priorityWrapper}>
                          <p className={styles.priorityHint}>
                            Arraste ou use as setas para ordenar (o topo é a{' '}
                            <strong>maior prioridade</strong>).
                          </p>

                          <ul className={styles.priorityList}>
                            {priorityOrderTeste.map((letra, idx) => (
                              <li
                                key={letra}
                                className={`${styles.priorityItem} ${dragStateTeste.overIdx === idx ? styles.dragOver : ''}`}
                                draggable
                                onDragStart={onDragStartPriorityTeste(idx)}
                                onDragOver={onDragOverPriorityTeste(idx)}
                                onDrop={onDropPriorityTeste(idx)}
                                onDragEnd={onDragEndPriorityTeste}
                                title="Arraste para reordenar"
                              >
                                <span className={styles.dragHandle} aria-hidden="true">⋮⋮</span>
                                <span className={styles.priorityLetter}>{letra}</span>
                                <span className={styles.priorityText}>{(perguntaAtualTeste.options || {})[letra]}</span>

                                <div className={styles.priorityActions}>
                                  <button
                                    type="button"
                                    className={`${styles.priorityBtn} ${styles.priorityBtnUp}`}
                                    aria-label="Mover para cima"
                                    onClick={handleClickUpTeste(idx)}
                                    disabled={idx === 0}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <FaArrowUp />
                                  </button>

                                  <button
                                    type="button"
                                    className={`${styles.priorityBtn} ${styles.priorityBtnDown}`}
                                    aria-label="Mover para baixo"
                                    onClick={handleClickDownTeste(idx)}
                                    disabled={idx === priorityOrderTeste.length - 1}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <FaArrowDown />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {perguntaAtualTeste?.type === 'scale' && scaleConfigTeste && (
                        <div className={styles.scaleWrapper}>
                          <div className={styles.scaleSliderHeader}>
                            <span className={styles.scaleMinLabel}>{scaleConfigTeste.minLabel}</span>
                            <span className={styles.scaleCurrentValue}>
                              {scaleConfigTeste.currentLabel}
                              {scaleConfigTeste.currentLabel !== String(scaleConfigTeste.valueNum) && <> ({scaleConfigTeste.valueNum})</>}
                            </span>
                            <span className={styles.scaleMaxLabel}>{scaleConfigTeste.maxLabel}</span>
                          </div>

                          <div className={styles.scaleSliderContainer}>
                            <input
                              type="range"
                              min={scaleConfigTeste.min}
                              max={scaleConfigTeste.max}
                              step={1}
                              value={scaleConfigTeste.valueNum}
                              onChange={(e) => handleChangeTeste(paginaAtualTeste, Number(e.target.value))}
                              className={styles.scaleSlider}
                            />
                          </div>

                          <div className={styles.scaleTicks}>
                            {scaleConfigTeste.ticks.map((tick) => (
                              <div key={tick.value} className={styles.scaleTick}>
                                <span className={styles.scaleTickLabel}>{tick.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {perguntaAtualTeste?.type === 'open' && (
                        perguntaAtualTeste?.hasDate ? (
                          <div className={styles.openWithDateWrapper}>
                            <input
                              type="text"
                              placeholder="Digite sua resposta"
                              value={
                                respostaAtualTeste && typeof respostaAtualTeste === 'object'
                                  ? (respostaAtualTeste.text || '')
                                  : (respostaAtualTeste || '')
                              }
                              onChange={(e) => {
                                const newText = e.target.value;
                                setRespostasTeste(prev => {
                                  const prevVal = prev[paginaAtualTeste];
                                  const prevObj = (prevVal && typeof prevVal === 'object')
                                    ? prevVal
                                    : { text: '', date: '' };
                                  return { ...prev, [paginaAtualTeste]: { ...prevObj, text: newText } };
                                });
                              }}
                              className={styles.textInput}
                              required
                            />

                            <input
                              type="date"
                              value={respostaAtualTeste && typeof respostaAtualTeste === 'object' ? (respostaAtualTeste.date || '') : ''}
                              onChange={(e) => {
                                const newDate = e.target.value;
                                setRespostasTeste(prev => {
                                  const prevVal = prev[paginaAtualTeste];
                                  const prevObj = (prevVal && typeof prevVal === 'object')
                                    ? prevVal
                                    : { text: '', date: '' };
                                  return { ...prev, [paginaAtualTeste]: { ...prevObj, date: newDate } };
                                });
                              }}
                              className={styles.dateInput}
                              required
                            />
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={typeof respostaAtualTeste === 'string' ? respostaAtualTeste : ''}
                            onChange={(e) => handleChangeTeste(paginaAtualTeste, e.target.value)}
                            className={styles.textInput}
                            required
                          />
                        )
                      )}
                    </div>

                    {/* Navegação do teste externo */}
                    <div className={styles.navigationArea} style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={handleAnteriorTeste}
                        disabled={paginaAtualTeste === 0}
                        className={styles.navBackButton}
                      >
                        Anterior
                      </button>

                      {paginaAtualTeste < (testeEstrutura?.questions?.length || 0) - 1 ? (
                        <button
                          type="button"
                          onClick={handleProximoTeste}
                          className={styles.navButton}
                          disabled={!perguntaRespondidaTeste}
                        >
                          Próxima
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmitTeste}
                          className={styles.submitButton}
                          disabled={!perguntaRespondidaTeste}
                        >
                          Enviar (console.log)
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {(detalhesSelecionado?.secoes || []).map((secao) => {
            const conteudos = detalhesSelecionado?.conteudosBySecao?.[secao.id] || [];

            return (
              <div key={secao.id} className={styles.perguntaWrapper}>
                <div className={styles.pergunta}>{secao.titulo}</div>

                {secao?.descricao ? <div className={styles.mutedNote}>{secao.descricao}</div> : null}

                <div className={styles.opcoesWrapper}>
                  {conteudos.length === 0 ? (
                    <div className={styles.mutedNote}>Nenhum conteúdo cadastrado nesta seção.</div>
                  ) : (
                    conteudos.map(renderConteudo)
                  )}
                </div>
              </div>
            );
          })}

          {detalhesSelecionado && detalhesSelecionado.secoes?.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Sem seções</div>
              <div className={styles.mutedNote}>Este módulo ainda não possui seções cadastradas.</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default EadTrilhaPage;